import React, { useState, useEffect } from "react";
import { useBuilding } from "../../context/BuildingContext";
import { db } from "../../firebase";
import { useToast } from "../../components/Toast";
import AppSelect from "../../components/AppSelect";
import { useIsMobile } from "../../hooks/useResponsive";
import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  Timestamp,
} from "firebase/firestore";
import {
  LuTriangleAlert,
  LuPlus,
  LuLoader,
  LuCircleCheck,
  LuClock,
  LuWrench,
  LuX,
} from "react-icons/lu";

const CATEGORIES = [
  { value: "sanitario", label: "Sanitario" },
  { value: "ascensor", label: "Ascensor" },
  { value: "electricidad", label: "Electricidad" },
  { value: "seguridad", label: "Seguridad" },
  { value: "limpieza", label: "Limpieza" },
  { value: "otro", label: "Otro" },
];

const PRIORITIES = [
  { value: "alta", label: "Alta" },
  { value: "media", label: "Media" },
  { value: "baja", label: "Baja" },
];

const IncidentesEmployee = ({ userid, userName }) => {
  const isMobile = useIsMobile();
  const { selectedBuilding } = useBuilding();
  const { showToast } = useToast();
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    title: "",
    description: "",
    category: "otro",
    priority: "media",
  });

  useEffect(() => {
    if (selectedBuilding) {
      loadIncidents();
    } else {
      setLoading(false);
    }
  }, [selectedBuilding]);

  const loadIncidents = async () => {
    setLoading(true);
    try {
      const buildingId = Number(selectedBuilding.buildingid);
      // Sin orderBy en la query: dos filtros de igualdad se resuelven con índices
      // de un campo (no requiere índice compuesto). Ordenamos en cliente.
      const q = query(
        collection(db, "incidents"),
        where("buildingid", "==", buildingId),
        where("reportedByEmployee", "==", String(userid))
      );
      const snap = await getDocs(q);
      const data = [];
      snap.forEach((d) => data.push({ id: d.id, ...d.data() }));
      data.sort((a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0));
      setIncidents(data);
    } catch (error) {
      console.error("Error loading incidents:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!form.title.trim() || !form.description.trim()) {
      showToast("Completá el título y la descripción.", "error");
      return;
    }
    setSubmitting(true);
    try {
      await addDoc(collection(db, "incidents"), {
        buildingid: Number(selectedBuilding.buildingid),
        userid: Number(userid),
        title: form.title.trim(),
        description: form.description.trim(),
        category: form.category,
        priority: form.priority,
        status: "abierto",
        createdAt: Timestamp.now(),
        reporterType: "employee",
        reportedByEmployee: Number(userid),
        reporterName: userName || "Empleado",
      });
      setForm({ title: "", description: "", category: "otro", priority: "media" });
      setShowForm(false);
      loadIncidents();
    } catch (error) {
      console.error("Error creating incident:", error);
      showToast("Error al crear el reporte.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusConfig = (status) => {
    const map = {
      abierto: { bg: "#fee2e2", color: "#991b1b", label: "Abierto", icon: LuTriangleAlert },
      en_proceso: { bg: "#fef3c7", color: "#92400e", label: "En proceso", icon: LuWrench },
      resuelto: { bg: "#d1fae5", color: "#065f46", label: "Resuelto", icon: LuCircleCheck },
    };
    return map[status] || map.abierto;
  };

  const getPriorityConfig = (priority) => {
    const map = {
      alta: { color: "#ef4444", bg: "#fee2e2" },
      media: { color: "#f59e0b", bg: "#fef3c7" },
      baja: { color: "#059669", bg: "#d1fae5" },
    };
    return map[priority] || map.media;
  };

  const getCategoryLabel = (cat) => {
    const found = CATEGORIES.find((c) => c.value === cat);
    return found ? found.label : cat;
  };

  const formatDate = (ts) => {
    if (!ts) return "—";
    const date = ts?.toDate ? ts.toDate() : new Date(ts);
    return date.toLocaleDateString("es-UY", { day: "2-digit", month: "short", year: "numeric" });
  };

  if (!selectedBuilding) {
    return (
      <div style={styles.emptyContainer}>
        <LuTriangleAlert size={48} color="#f59e0b" />
        <h2 style={styles.emptyTitle}>Sin edificio asignado</h2>
      </div>
    );
  }

  return (
    <div style={{ ...styles.container, padding: isMobile ? 16 : 24 }}>
      {/* Header */}
      <div style={styles.pageHeader}>
        <div>
          <h1 style={styles.pageTitle}>Reportes</h1>
          <p style={styles.pageSubtitle}>{selectedBuilding.name}</p>
        </div>
        <button style={styles.newBtn} onClick={() => setShowForm(!showForm)}>
          {showForm ? <LuX size={14} /> : <LuPlus size={14} />}
          {showForm ? "Cancelar" : "Nuevo Reporte"}
        </button>
      </div>

      {/* New Incident Form */}
      {showForm && (
        <div style={styles.formCard}>
          <h2 style={styles.formTitle}>Nuevo Reporte</h2>
          <div style={styles.formGroup}>
            <label style={styles.formLabel}>Título</label>
            <input
              type="text"
              style={styles.formInput}
              placeholder="Descripción breve del problema"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
            />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.formLabel}>Descripción</label>
            <textarea
              style={styles.formTextarea}
              placeholder="Describí el reporte en detalle..."
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={4}
            />
          </div>
          <div style={{ ...styles.formRow, flexDirection: isMobile ? "column" : "row" }}>
            <div style={{ ...styles.formGroup, flex: 1, minWidth: 0 }}>
              <label style={styles.formLabel}>Categoría</label>
              <AppSelect
                value={form.category}
                onChange={(v) => setForm({ ...form, category: v })}
                options={CATEGORIES}
                ariaLabel="Categoría"
              />
            </div>
            <div style={{ ...styles.formGroup, flex: 1, minWidth: 0 }}>
              <label style={styles.formLabel}>Prioridad</label>
              <AppSelect
                value={form.priority}
                onChange={(v) => setForm({ ...form, priority: v })}
                options={PRIORITIES}
                ariaLabel="Prioridad"
              />
            </div>
          </div>
          <div style={styles.formActions}>
            <button style={styles.cancelBtn} onClick={() => setShowForm(false)}>
              Cancelar
            </button>
            <button
              style={{ ...styles.submitBtn, opacity: submitting ? 0.7 : 1 }}
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting ? (
                <LuLoader size={14} style={{ animation: "spin 1s linear infinite" }} />
              ) : (
                <LuTriangleAlert size={14} />
              )}
              Nuevo Reporte
            </button>
          </div>
        </div>
      )}

      {/* Incidents List */}
      <h2 style={styles.sectionTitle}>Mis Reportes</h2>
      {loading ? (
        <div style={styles.loadingCenter}>
          <LuLoader size={24} color="#0080ff" style={{ animation: "spin 1s linear infinite" }} />
        </div>
      ) : incidents.length === 0 ? (
        <div style={styles.emptyState}>
          <LuCircleCheck size={40} color="#d1d5db" />
          <p style={{ color: "#6b7280", fontSize: 14, marginTop: 12 }}>
            No tenés reportes creados.
          </p>
        </div>
      ) : (
        <div style={styles.list}>
          {incidents.map((inc) => {
            const statusCfg = getStatusConfig(inc.status);
            const priorityCfg = getPriorityConfig(inc.priority);
            const StatusIcon = statusCfg.icon;

            return (
              <div key={inc.id} style={styles.card}>
                <div style={styles.cardHeader}>
                  <div style={styles.cardHeaderLeft}>
                    <div style={styles.cardTags}>
                      <span style={styles.categoryTag}>
                        {getCategoryLabel(inc.category)}
                      </span>
                      <span style={{
                        ...styles.priorityTag,
                        backgroundColor: priorityCfg.bg,
                        color: priorityCfg.color,
                      }}>
                        {inc.priority === "alta" ? "Alta" : inc.priority === "media" ? "Media" : "Baja"}
                      </span>
                    </div>
                    <h3 style={styles.cardTitle}>{inc.title}</h3>
                  </div>
                  <span style={{
                    ...styles.statusBadge,
                    backgroundColor: statusCfg.bg,
                    color: statusCfg.color,
                  }}>
                    <StatusIcon size={10} />
                    {statusCfg.label}
                  </span>
                </div>
                <p style={styles.cardDesc}>{inc.description}</p>
                <div style={styles.cardMeta}>
                  <span style={styles.metaItem}>
                    <LuClock size={11} />
                    {formatDate(inc.createdAt)}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    padding: 24,
    maxWidth: 900,
    margin: "0 auto",
    fontFamily: "'FT Habit', -apple-system, BlinkMacSystemFont, sans-serif",
  },
  emptyContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "60vh",
    gap: 12,
  },
  emptyTitle: {
    fontSize: 20,
    fontWeight: 400,
    color: "#111827",
  },
  pageHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 20,
  },
  pageTitle: {
    fontSize: 24,
    fontWeight: 400,
    color: "#111827",
    margin: 0,
  },
  pageSubtitle: {
    fontSize: 14,
    color: "#6b7280",
    margin: "4px 0 0",
  },
  newBtn: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "10px 20px",
    backgroundColor: "#0080ff",
    color: "#ffffff",
    border: "none",
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 400,
    cursor: "pointer",
    fontFamily: "'FT Habit', -apple-system, BlinkMacSystemFont, sans-serif",
  },
  formCard: {
    backgroundColor: "#ffffff",
    borderRadius: 12,
    border: "1px solid #e5e7eb",
    boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
    padding: 24,
    marginBottom: 24,
  },
  formTitle: {
    fontSize: 18,
    fontWeight: 400,
    color: "#111827",
    margin: "0 0 20px",
  },
  formGroup: {
    marginBottom: 16,
  },
  formLabel: {
    display: "block",
    fontSize: 13,
    fontWeight: 400,
    color: "#374151",
    marginBottom: 6,
  },
  formInput: {
    width: "100%",
    padding: "10px 12px",
    border: "1px solid #e5e7eb",
    borderRadius: 8,
    fontSize: 14,
    outline: "none",
    fontFamily: "'FT Habit', -apple-system, BlinkMacSystemFont, sans-serif",
    boxSizing: "border-box",
  },
  formTextarea: {
    width: "100%",
    padding: "10px 12px",
    border: "1px solid #e5e7eb",
    borderRadius: 8,
    fontSize: 14,
    outline: "none",
    fontFamily: "'FT Habit', -apple-system, BlinkMacSystemFont, sans-serif",
    resize: "vertical",
    boxSizing: "border-box",
  },
  formSelect: {
    width: "100%",
    padding: "10px 12px",
    border: "1px solid #e5e7eb",
    borderRadius: 8,
    fontSize: 14,
    outline: "none",
    fontFamily: "'FT Habit', -apple-system, BlinkMacSystemFont, sans-serif",
    backgroundColor: "#ffffff",
    boxSizing: "border-box",
  },
  formRow: {
    display: "flex",
    gap: 12,
  },
  formActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 10,
    marginTop: 8,
  },
  cancelBtn: {
    padding: "10px 20px",
    backgroundColor: "#ffffff",
    color: "#374151",
    border: "1px solid #e5e7eb",
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 400,
    cursor: "pointer",
    fontFamily: "'FT Habit', -apple-system, BlinkMacSystemFont, sans-serif",
  },
  submitBtn: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "10px 20px",
    backgroundColor: "#ef4444",
    color: "#ffffff",
    border: "none",
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 400,
    cursor: "pointer",
    fontFamily: "'FT Habit', -apple-system, BlinkMacSystemFont, sans-serif",
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 400,
    color: "#111827",
    margin: "0 0 16px",
  },
  loadingCenter: {
    display: "flex",
    justifyContent: "center",
    padding: 48,
  },
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: 48,
  },
  list: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
  card: {
    backgroundColor: "#ffffff",
    borderRadius: 12,
    padding: 20,
    border: "1px solid #e5e7eb",
    boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
  },
  cardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 8,
  },
  cardHeaderLeft: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },
  cardTags: {
    display: "flex",
    gap: 6,
  },
  categoryTag: {
    fontSize: 11,
    fontWeight: 400,
    color: "#0080ff",
    backgroundColor: "#f0f7ff",
    padding: "2px 8px",
    borderRadius: 4,
  },
  priorityTag: {
    fontSize: 11,
    fontWeight: 400,
    padding: "2px 8px",
    borderRadius: 4,
  },
  cardTitle: {
    fontSize: 16,
    fontWeight: 400,
    color: "#111827",
    margin: 0,
  },
  statusBadge: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    fontSize: 12,
    fontWeight: 400,
    padding: "4px 12px",
    borderRadius: 20,
    flexShrink: 0,
  },
  cardDesc: {
    fontSize: 14,
    color: "#6b7280",
    margin: "0 0 12px",
    lineHeight: 1.5,
  },
  cardMeta: {
    display: "flex",
    gap: 16,
  },
  metaItem: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    fontSize: 12,
    color: "#6b7280",
  },
};

export default IncidentesEmployee;
